import type { FC, ReactNode } from 'react';

// eslint-disable-next-line no-restricted-imports -- fe-import-rule-disable: allow - shared feature-flag package is the sanctioned LaunchDarkly SDK boundary.
import { LDProvider } from 'launchdarkly-react-client-sdk';
import type { LDContext } from 'launchdarkly-react-client-sdk';

/**
 * The evaluation context shape. Re-exported so an app can name the type it builds without importing
 * the LaunchDarkly SDK: this package is the sanctioned SDK boundary.
 */
export type { LDContext };

export type FeatureFlagProviderProps = {
  /** LaunchDarkly client-side id for this environment. */
  clientSideID: string;
  /**
   * The evaluation context (user/org). Pass `undefined` while auth is still
   * initializing; `deferInitialization` keeps LD from evaluating until a context
   * is provided.
   */
  context: LDContext | undefined;
  children: ReactNode;
};

/**
 * Vendor-neutral feature-flag provider. Wraps LaunchDarkly's `LDProvider` with
 * the ModernLoop defaults shared by every app (deferred initialization,
 * non-camel-cased flag keys, localStorage bootstrap). Swapping providers means
 * reimplementing the small internals of this package, not touching apps.
 */
export const FeatureFlagProvider: FC<FeatureFlagProviderProps> = ({ clientSideID, context, children }) => (
  <LDProvider
    clientSideID={clientSideID}
    deferInitialization
    reactOptions={{ useCamelCaseFlagKeys: false }}
    options={{ bootstrap: 'localStorage' }}
    context={context}
  >
    {children}
  </LDProvider>
);
